import React, { useState, useRef, useEffect } from 'react';
import SignatureCanvas from 'react-signature-canvas';
import '../styles/EnrollmentStep2.css';
import EnrollmentContractPreview, { type EnrollmentContractPreviewData } from './EnrollmentContractPreview';

export interface EnrollmentConfirmation {
  personName: string;
  photoUrl: string;
  qrCodeUrl: string;
  siteName: string;
  siteAddress1: string;
  siteAddress2: string;
}

export interface EnrollmentReserveResponse {
  success: boolean;
  message: string;
  personId?: string;
  qrCode?: string;
  contractId?: string;
  confirmation?: EnrollmentConfirmation;
}

export interface EnrollmentStep2Props {
  companyId: string;
  siteId: number;
  packageId: number;
  enrollmentKey: string;
  apiUrl?: string;
  onSuccess: (response: EnrollmentReserveResponse) => void;
  onCancel?: () => void;
  recaptchaSiteKey?: string;
  /** JWT of the already-authenticated caller (e.g. a logged-in PWA member).
   * When present, sent as a Bearer token so the backend can resolve the
   * existing PersonID instead of matching by email text alone. Omit for the
   * anonymous guest-enrollment flow (FitTrackOnline). */
  authToken?: string;
  prefill?: {
    address?: string;
    city?: string;
    state?: string;
    zipCode?: string;
    birthDate?: string;
    genderTypeId?: number;
  };
}

interface ReserveFormData {
  address: string;
  city: string;
  state: string;
  zipCode: string;
  birthDate: string;
  genderTypeId: number;
  creditCard: string;
  expirationMonth: string;
  expirationYear: number;
  cvv: string;
}

const EnrollmentStep2: React.FC<EnrollmentStep2Props> = ({
  companyId,
  siteId,
  packageId,
  enrollmentKey,
  apiUrl = '',
  onSuccess,
  onCancel,
  recaptchaSiteKey,
  authToken,
  prefill
}) => {
  const [formData, setFormData] = useState<ReserveFormData>({
    address: prefill?.address ?? '',
    city: prefill?.city ?? '',
    state: prefill?.state ?? '',
    zipCode: prefill?.zipCode ?? '',
    birthDate: prefill?.birthDate ?? '',
    genderTypeId: prefill?.genderTypeId ?? 0,
    creditCard: '',
    expirationMonth: '',
    expirationYear: new Date().getFullYear(),
    cvv: ''
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [showTermsModal, setShowTermsModal] = useState(false);

  // Contract preview state
  const [contractPreview, setContractPreview] = useState<EnrollmentContractPreviewData | null>(null);

  // AB#68: whether this package has any nonzero payment due (now or later). Defaults
  // to true (fail safe toward requiring a card) until the real value loads.
  const [requiresPaymentMethod, setRequiresPaymentMethod] = useState(true);

  const signaturePadMain = useRef<SignatureCanvas>(null);
  const signaturePadMinor = useRef<SignatureCanvas>(null);

  const usStates = [
    'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
    'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
    'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
    'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
    'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY'
  ];

  const months = Array.from({ length: 12 }, (_, i) => {
    const month = (i + 1).toString().padStart(2, '0');
    return { value: month, label: month };
  });

  const years = Array.from({ length: 20 }, (_, i) => {
    const year = new Date().getFullYear() + i;
    return { value: year, label: year.toString() };
  });

  const genderTypes = [
    { value: 1, label: 'Male' },
    { value: 2, label: 'Female' },
    { value: 3, label: 'Other' }
  ];

  // Resize signature canvases on mount
  useEffect(() => {
    const resizeCanvas = (canvas: SignatureCanvas | null) => {
      if (canvas && canvas.getCanvas()) {
        const canvasEl = canvas.getCanvas();
        const ratio = Math.max(window.devicePixelRatio || 1, 1);
        canvasEl.width = canvasEl.offsetWidth * ratio;
        canvasEl.height = canvasEl.offsetHeight * ratio;
        canvasEl.getContext('2d')?.scale(ratio, ratio);
        canvas.clear();
      }
    };

    resizeCanvas(signaturePadMain.current);
    resizeCanvas(signaturePadMinor.current);

    const handleResize = () => {
      resizeCanvas(signaturePadMain.current);
      resizeCanvas(signaturePadMinor.current);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Fetch contract preview on mount
  useEffect(() => {
    if (!enrollmentKey) return;
    const url = apiUrl
      ? `${apiUrl}/api/packageenrollment/contractpreview/${enrollmentKey}`
      : `/api/packageenrollment/contractpreview/${enrollmentKey}`;
    fetch(url, { headers: { 'X-Tenant-ID': companyId, ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}) } })
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (data?.success) setContractPreview(data);
      })
      .catch(() => { /* non-fatal */ });
  }, [enrollmentKey, apiUrl, companyId]);

  // AB#68: look up whether this package ever charges anything, so we can skip
  // asking for a payment method entirely for packages with no payments due.
  useEffect(() => {
    if (!packageId || !siteId) return;
    const url = apiUrl
      ? `${apiUrl}/api/package/${packageId}/join/${siteId}`
      : `/api/package/${packageId}/join/${siteId}`;
    fetch(url, { headers: { 'X-Tenant-ID': companyId, ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}) } })
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (typeof data?.pricing?.requiresPaymentMethod === 'boolean') {
          setRequiresPaymentMethod(data.pricing.requiresPaymentMethod);
        }
      })
      .catch(() => { /* fail safe: leave the default (true) in place */ });
  }, [packageId, siteId, apiUrl, companyId, authToken]);

  // Visual top-to-bottom order of the fields validateForm checks, so a failed
  // submit can scroll to whichever one of them is actually blocking it.
  const fieldOrder = [
    'address', 'city', 'state', 'zipCode', 'birthDate', 'genderTypeId',
    'creditCard', 'expirationMonth', 'expirationYear', 'cvv', 'signature'
  ];

  const validateForm = (): Record<string, string> => {
    const newErrors: Record<string, string> = {};

    if (!formData.address.trim()) newErrors.address = 'Address is required';
    if (!formData.city.trim()) newErrors.city = 'City is required';
    if (!formData.state) newErrors.state = 'State is required';
    if (!formData.zipCode.trim()) newErrors.zipCode = 'Zip code is required';
    if (!formData.birthDate) newErrors.birthDate = 'Birth date is required';
    if (!formData.genderTypeId) newErrors.genderTypeId = 'Gender is required';

    // AB#68: packages with nothing ever due don't need a payment method on file.
    if (requiresPaymentMethod) {
      if (!formData.creditCard.trim()) newErrors.creditCard = 'Credit card is required';
      if (!formData.expirationMonth) newErrors.expirationMonth = 'Expiration month is required';
      if (!formData.expirationYear) newErrors.expirationYear = 'Expiration year is required';
      if (!formData.cvv.trim()) newErrors.cvv = 'CVV is required';
    }

    // Validate signature
    if (signaturePadMain.current?.isEmpty()) {
      newErrors.signature = 'Signature is required';
    }

    return newErrors;
  };

  // Scrolls to and focuses the first invalid field (in visual order) so the
  // user isn't left looking at the submit button with no idea what's wrong.
  const scrollToFirstError = (validationErrors: Record<string, string>) => {
    const firstField = fieldOrder.find(field => validationErrors[field]);
    if (!firstField) return;

    const el = document.querySelector(`[data-field="${firstField}"]`);
    if (!el) return;

    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    if (el instanceof HTMLElement && typeof el.focus === 'function') {
      el.focus({ preventScroll: true });
    }
  };

  const formatCreditCard = (value: string): string => {
    const cleaned = value.replace(/\D/g, '');
    const match = cleaned.match(/^(\d{0,4})(\d{0,4})(\d{0,4})(\d{0,4})$/);
    if (match) {
      return [match[1], match[2], match[3], match[4]].filter(Boolean).join(' ');
    }
    return value;
  };

  const handleChange = (field: keyof ReserveFormData, value: any) => {
    if (field === 'creditCard') {
      value = formatCreditCard(value);
    } else if (field === 'cvv') {
      value = value.replace(/\D/g, '');
    }

    setFormData(prev => ({ ...prev, [field]: value }));

    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const getRecaptchaToken = async (): Promise<string> => {
    if (!recaptchaSiteKey || !window.grecaptcha) {
      return '';
    }

    try {
      const token = await window.grecaptcha.execute(recaptchaSiteKey, { action: 'reserve' });
      return token;
    } catch (error) {
      console.error('reCAPTCHA error:', error);
      return '';
    }
  };

  const handleClearSignature = () => {
    signaturePadMain.current?.clear();
    if (errors.signature) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.signature;
        return newErrors;
      });
    }
  };

  const handleClearMinorSignature = () => {
    signaturePadMinor.current?.clear();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage('');

    const validationErrors = validateForm();
    setErrors(validationErrors);
    if (Object.keys(validationErrors).length > 0) {
      scrollToFirstError(validationErrors);
      return;
    }

    try {
      setIsSubmitting(true);

      // Get signatures as base64 data URLs
      const signatureMain = signaturePadMain.current?.toDataURL() || '';
      const signatureMinor = signaturePadMinor.current?.isEmpty()
        ? null
        : signaturePadMinor.current?.toDataURL();

      // Get reCAPTCHA token
      const recaptchaToken = await getRecaptchaToken();

      const url = apiUrl ? `${apiUrl}/api/packageenrollment/signupreserve` : '/api/packageenrollment/signupreserve';
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Tenant-ID': companyId,
          ...(authToken ? { Authorization: `Bearer ${authToken}` } : {})
        },
        body: JSON.stringify({
          enrollmentKey,
          ...formData,
          signatureMain,
          signatureMinor,
          recaptchaToken
        })
      });

      const result: EnrollmentReserveResponse = await response.json();

      if (response.ok && result.success) {
        onSuccess(result);
      } else {
        setErrorMessage(result.message || 'Failed to complete enrollment');
      }
    } catch (error: any) {
      setErrorMessage(error.message || 'An error occurred during enrollment');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="enrollment-step2-container">
      <div className="enrollment-step2-header">
        <h1>Complete Your Enrollment</h1>
      </div>

      <form onSubmit={handleSubmit} className="enrollment-step2-form">
        <h3>Personal Information</h3>

        <div className="form-row">
          <div className="form-column">
            <div className="form-group">
              {/* Address has no visible label (placeholder-only), but Birth Date in
                  the column beside it does -- without this, Birth Date's label pushes
                  its input down and the two columns' first row no longer lines up.
                  Same text/markup as the real label so font metrics match exactly;
                  hidden from sight and from screen readers since it carries no
                  information of its own. */}
              <label className="label-spacer" aria-hidden="true">Birth Date</label>
              <input
                type="text"
                className="form-control"
                placeholder="Address"
                value={formData.address}
                onChange={(e) => handleChange('address', e.target.value)}
                disabled={isSubmitting}
                readOnly={!!prefill?.address}
                autoComplete="street-address"
                autoCapitalize="words"
                enterKeyHint="next"
                data-field="address"
              />
              {errors.address && <span className="error-text">{errors.address}</span>}
            </div>

            <div className="form-group">
              <input
                type="text"
                className="form-control"
                placeholder="City"
                value={formData.city}
                onChange={(e) => handleChange('city', e.target.value)}
                disabled={isSubmitting}
                readOnly={!!prefill?.city}
                autoComplete="address-level2"
                autoCapitalize="words"
                enterKeyHint="next"
                data-field="city"
              />
              {errors.city && <span className="error-text">{errors.city}</span>}
            </div>

            <div className="form-group">
              <select
                className="form-control"
                value={formData.state}
                onChange={(e) => handleChange('state', e.target.value)}
                disabled={!!prefill?.state || isSubmitting}
                autoComplete="address-level1"
                data-field="state"
              >
                <option value="">State</option>
                {usStates.map(state => (
                  <option key={state} value={state}>{state}</option>
                ))}
              </select>
              {errors.state && <span className="error-text">{errors.state}</span>}
            </div>

            <div className="form-group">
              <input
                type="text"
                inputMode="numeric"
                className="form-control"
                placeholder="Zip"
                value={formData.zipCode}
                onChange={(e) => handleChange('zipCode', e.target.value)}
                disabled={isSubmitting}
                readOnly={!!prefill?.zipCode}
                maxLength={10}
                autoComplete="postal-code"
                enterKeyHint="next"
                data-field="zipCode"
              />
              {errors.zipCode && <span className="error-text">{errors.zipCode}</span>}
            </div>
          </div>

          <div className="form-column">
            <div className="form-group">
              <label>Birth Date</label>
              <input
                type="date"
                className={`form-control ${errors.birthDate ? 'error' : ''}`}
                value={formData.birthDate}
                onChange={(e) => handleChange('birthDate', e.target.value)}
                disabled={isSubmitting}
                readOnly={!!prefill?.birthDate}
                min={new Date(new Date().getFullYear() - 100, 0, 1).toISOString().split('T')[0]}
                max={new Date(new Date().getFullYear() - 18, 11, 31).toISOString().split('T')[0]}
                data-field="birthDate"
              />
              {errors.birthDate && <span className="error-text">{errors.birthDate}</span>}
            </div>

            <div className="form-group">
              <select
                className="form-control"
                value={formData.genderTypeId}
                onChange={(e) => handleChange('genderTypeId', parseInt(e.target.value))}
                disabled={!!prefill?.genderTypeId || isSubmitting}
                data-field="genderTypeId"
              >
                <option value="">Gender</option>
                {genderTypes.map(gender => (
                  <option key={gender.value} value={gender.value}>{gender.label}</option>
                ))}
              </select>
              {errors.genderTypeId && <span className="error-text">{errors.genderTypeId}</span>}
            </div>
          </div>
        </div>

        {requiresPaymentMethod && (
          <>
            <hr />
            <h3>Payment Information</h3>

            <div className="cc-image-row">
              <img src="/assets/images/ccimage.png" width="135px" height="37px" alt="Credit Cards" />
            </div>

            <div className="payment-row">
              <input
                type="text"
                inputMode="numeric"
                className="form-control cc-input"
                placeholder="0000 0000 0000 0000"
                value={formData.creditCard}
                onChange={(e) => handleChange('creditCard', e.target.value)}
                disabled={isSubmitting}
                maxLength={19}
                autoComplete="cc-number"
                enterKeyHint="next"
                data-field="creditCard"
              />
              <select
                className="form-control exp-month"
                value={formData.expirationMonth}
                onChange={(e) => handleChange('expirationMonth', e.target.value)}
                disabled={isSubmitting}
                autoComplete="cc-exp-month"
                data-field="expirationMonth"
              >
                <option value="">MM</option>
                {months.map(m => (
                  <option key={m.value} value={m.value}>{m.label}</option>
                ))}
              </select>
              <select
                className="form-control exp-year"
                value={formData.expirationYear}
                onChange={(e) => handleChange('expirationYear', parseInt(e.target.value))}
                disabled={isSubmitting}
                autoComplete="cc-exp-year"
                data-field="expirationYear"
              >
                <option value="">YYYY</option>
                {years.map(y => (
                  <option key={y.value} value={y.value}>{y.label}</option>
                ))}
              </select>
              <input
                type="text"
                inputMode="numeric"
                className="form-control cvv-input"
                placeholder="CVV"
                value={formData.cvv}
                onChange={(e) => handleChange('cvv', e.target.value)}
                disabled={isSubmitting}
                maxLength={4}
                autoComplete="cc-csc"
                enterKeyHint="done"
                data-field="cvv"
              />
            </div>
            {errors.creditCard && <span className="error-text">{errors.creditCard}</span>}
            {errors.expirationMonth && <span className="error-text">{errors.expirationMonth}</span>}
            {errors.expirationYear && <span className="error-text">{errors.expirationYear}</span>}
            {errors.cvv && <span className="error-text">{errors.cvv}</span>}
          </>
        )}

        <hr />
        <h3>Signature</h3>

        <p className="signature-intro">
          Sign below to agree to the{' '}
          <a
            href="#"
            className="terms-link"
            onClick={(e) => { e.preventDefault(); setShowTermsModal(true); }}
          >
            Terms and Conditions
          </a>
        </p>

        <div className="signature-section">
          <div className="signature-box" data-field="signature">
            <label>Your Signature *</label>
            <div className="signature-pad-wrapper">
              <SignatureCanvas
                ref={signaturePadMain}
                canvasProps={{ className: 'signature-canvas' }}
                backgroundColor="rgb(255, 255, 255)"
              />
            </div>
            <button type="button" onClick={handleClearSignature} className="btn-clear">
              Clear
            </button>
            {errors.signature && <span className="error-text">{errors.signature}</span>}
          </div>

          <div className="signature-box" style={{ display: 'none' }}>
            <label>Minor Signature (if applicable)</label>
            <div className="signature-pad-wrapper">
              <SignatureCanvas
                ref={signaturePadMinor}
                canvasProps={{ className: 'signature-canvas' }}
                backgroundColor="rgb(255, 255, 255)"
              />
            </div>
            <button type="button" onClick={handleClearMinorSignature} className="btn-clear">
              Clear
            </button>
          </div>
        </div>

        {errorMessage && <div className="error-message">{errorMessage}</div>}

        <div className="form-actions">
          {onCancel && (
            <button type="button" onClick={onCancel} className="btn-secondary" disabled={isSubmitting}>
              Cancel
            </button>
          )}
          <button type="submit" className="btn-primary" disabled={isSubmitting}>
            {isSubmitting ? 'Processing...' : 'Complete Enrollment'}
          </button>
        </div>
      </form>

      {showTermsModal && (
        /* Read-only contract preview — no signature section */
        <div className="enrollment-contract-modal-overlay" onClick={() => setShowTermsModal(false)}>
          <div className="enrollment-contract-modal" onClick={(e) => e.stopPropagation()}>
            <div className="enrollment-contract-modal-header">
              <h2>Membership Agreement</h2>
              <button className="enrollment-contract-modal-close" onClick={() => setShowTermsModal(false)} aria-label="Close">✕</button>
            </div>
            <div className="enrollment-contract-modal-body">
              {contractPreview ? (
                <EnrollmentContractPreview
                  data={contractPreview}
                  formData={{
                    address: formData.address,
                    city: formData.city,
                    state: formData.state,
                    zipCode: formData.zipCode,
                    birthDate: formData.birthDate,
                    genderTypeId: formData.genderTypeId
                  }}
                />
              ) : (
                <p>Contract details are not available. Please contact the front desk.</p>
              )}
            </div>
            <div className="enrollment-contract-modal-footer">
              <button className="btn-primary" onClick={() => setShowTermsModal(false)}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EnrollmentStep2;

// Extend Window interface for reCAPTCHA
declare global {
  interface Window {
    grecaptcha: any;
  }
}
